/* eslint-disable complexity */
import { DownOutlined } from '@ant-design/icons';
import { Checkbox, Col, Form, Radio, Space } from 'antd';
import { useWatch } from 'antd/es/form/Form';
import SidePanel from 'common/side-panel/side-panel';
import Spinner from 'common/spinner/components/spinner';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import styled from 'styled-components';
import { Button, DatePicker, Dropdown, Flex, FormField, Icon, Input, InputNumber, Row } from 'ui';
import useDeepCompareEffect from 'use-deep-compare-effect';
import moment from 'utils/cops-moment';
import RichTextEditor from '../../../ui/rich-text-editor';
import { planningBoardWorklistId, ticketWorklistId } from '../../../utils/app-configs';
import { isObjectNullOrEmpty } from '../../../utils/helpers';
import {
  addCampaign,
  fetchWizardData,
  resetCampaignData,
  updateCampaign,
} from '../../containers/campaigns/campaigns-actions';


const StyledButton = styled(Button)`
  text-transform: uppercase;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
`;

const StyledRow = styled(Row)`
  margin-bottom: 20px;
  .ant-form-item-label {
    width: 25%;
    word-break: break-word;
    text-align: start;
    white-space: pre-wrap;
  }
`;

const StyledRowWithLink = styled(StyledRow)`
  position: relative;
`;

const StyledFormField = styled(FormField)`
  .ant-form-item-label label {
    height: 36px !important;
  }
`;

const Buttons = styled(Row)`
  gap: 20px;
`;

const SubForm = styled.div`
  width: 75%;
  margin-left: 25%;
`;

const DateFormField = styled(FormField)`
  .ant-form-item-control-input-content,
  .ant-picker {
    height: 100%;
  }
`;

const StyledFlex = styled(Flex)`
  gap: 10px;
  .ant-picker {
    height: 32px;
  }
  .ant-input-number {
    width: 65px;
    padding-left: 0px !important;
  }
`;

const StyledDropdown = styled(Dropdown)`
  width: 75% !important;
  margin-right: 10px;
`;

const OpenLink = styled.div`
  position: absolute;
  top: 5px;
  right: 0;
  text-decoration: underline;
  color: ${p => p.theme.colors.theme};
  cursor: pointer;
  i {
    margin-left: 5px;
    cursor: pointer;
  }
`;

const OccurencesFormField = styled(FormField)`
 .ant-col.ant-form-item-control,
 .ant-form-item-control-input {
  position: unset;
 }
 #occurrences_help {
  position: absolute;
  left: 0;
  width: max-content;
 }
`;

const plannerGroupId = 'PlannerGroup';

const EditCampaignSidepanel = ({
  intl,
}) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const {
    updateCampaignSuccess,
    isUpdatingCampaign,
    isLoadingWizardData,
    wizardData = {},
    isLoadingCampaign,
  } = useSelector(state => state.campaigns);
  const [isFormTouched, setIsFormTouched] = useState(false);
  const [worklistViews, setWorklistViews] = useState([]);
  const [worklistActions, setWorklistActions] = useState([]);
  const [selectedView, setSelectedView] = useState();
  const [worklistId, setWorklistId] = useState('');
  const [calculationOptions, setCalculationOptions] = useState([]);
  const startDateValue = useWatch('startDate', form);
  const useOccurrences = useWatch('useOccurrences', form);
  const isSidePanelLoading =
    isLoadingCampaign ||
    isLoadingWizardData ||
    (params?.id && isObjectNullOrEmpty(wizardData?.campaign));

  useEffect(() => {
    dispatch(fetchWizardData({ id: params?.id }));
  }, []);

  useEffect(() => {
    if (!useOccurrences && !form.getFieldValue('occurrences')) {
      form.setFieldsValue({
        occurrences: 1,
      });
    }
  }, [useOccurrences]);

  const canGroupByPlannerGroup = wizardData?.groupableColumns &&
    worklistId &&
    !!wizardData?.groupableColumns[worklistId]?.find(item => item.id === 'PlannerGroup');

  useEffect(() => {
    // eslint-disable-next-line no-nested-ternary
    setWorklistViews(worklistId ?
      params?.id ? wizardData?.campaignViews[worklistId] : wizardData?.worklistViews[worklistId] :
      [],
    );
    setWorklistActions(worklistId ?
      wizardData?.actions[worklistId] :
      [],
    );
    if (!canGroupByPlannerGroup && form.getFieldValue('groupByPlannerGroup')) {
      form.setFieldsValue({
        groupByPlannerGroup: false,
      });
    }
  }, [worklistId]);

  useDeepCompareEffect(() => {
    if (worklistViews) {
      form.setFieldsValue({
        worklistView: wizardData?.campaign?.worklistViewId,
      });
      setSelectedView(wizardData?.campaign?.worklistViewId);
    }
  }, [worklistViews]);

  useDeepCompareEffect(() => {
    if (worklistActions) {
      form.setFieldsValue({
        actions: wizardData?.campaign?.actions.map(a => a.id),
      });
    }
  }, [worklistActions]);

  useDeepCompareEffect(() => {
    if (!isObjectNullOrEmpty(wizardData)) {
      const { campaign } = wizardData;
      if (params?.id) {
        setWorklistId(campaign.worklistType);
        form.setFieldsValue({
          title: campaign.title,
          worklist: campaign.worklistType,
          calculationAmount: campaign.calculationInfo.amount,
          frequency: campaign.calculationInfo.frequency,
          startDate: campaign.calculationInfo.startDate ?
            moment(campaign.calculationInfo.startDate) :
            null,
          endDate: campaign.calculationInfo.endDate ?
            moment(campaign.calculationInfo.endDate) :
            null,
          occurrences: campaign.calculationInfo.occurrences || 1,
          scopes: campaign.scopes?.map(s => s.id),
          locations: campaign.locations,
          instruction: campaign.instruction,
          useOccurrences: !!campaign.calculationInfo.occurrences,
          groupByPlannerGroup:
            campaign.groupByColumn?.toLowerCase() === plannerGroupId?.toLowerCase(),
        });
      } else {
        form.setFieldsValue({
          useOccurrences: false,
          calculationAmount: 10,
          frequency: wizardData?.frequencies?.find(f => f.isDefault)?.id || 'weekly',
          occurrences: 1,
          locations: wizardData?.locations?.filter(f => f.isDefault)?.map(s => s.id) || [],
          startDate: moment().set({ hours: 0, minutes: 0, seconds: 0 }).add(1, 'day'),
          endDate: moment().set({ hours: 0, minutes: 0, seconds: 0 }).add(2, 'day'),
          groupByPlannerGroup: false,
        });
      }
    }

    const {
      maxAmount = 50,
      minAmount = 5,
      incrementsOf = 5,
    } = wizardData;
    setCalculationOptions(
      Array.from(
        { length: ((maxAmount - minAmount) / incrementsOf) + 1 },
        (_, i) => minAmount + (i * incrementsOf),
      )
        ?.map(o => ({
          id: o,
          description: intl.formatMessage({ id: 'campaigns.edit.TOP' }, { amount: o }),
        })),
    );
  }, [wizardData]);

  const close = () => {
    dispatch(resetCampaignData());
    form.resetFields();
    navigate(-1);
  };

  useEffect(() => {
    if (updateCampaignSuccess) {
      close();
    }
  }, [updateCampaignSuccess]);

  const handleSave = () => {
    const formValues = form.getFieldsValue();
    const dataToSend = {
      title: formValues.title,
      amount: formValues.calculationAmount,
      frequency: formValues.frequency,
      startDate: formValues.startDate.format('YYYY-MM-DDTHH:mm:ss'),
      occurrences: formValues.useOccurrences ? formValues.occurrences : null,
      endDate: !formValues.useOccurrences ? formValues.endDate.format('YYYY-MM-DDTHH:mm:ss') : null,
      scopeIds: formValues.scopes,
      locations: formValues.locations,
      instruction: formValues.instruction,
      actions: formValues.actions,
      viewId: formValues.worklistView,
      worklistType: formValues.worklist,
      groupByColumn: formValues.groupByPlannerGroup ? plannerGroupId : null,
    };

    if (params?.id) {
      dispatch(updateCampaign({ id: params?.id, ...dataToSend }));
    } else {
      dispatch(addCampaign(dataToSend));
    }
  };

  const endDateValidators = (_, value) => {
    if (!value) {
      return Promise.reject(new Error(intl.formatMessage({
        id: 'campaigns.rules.CAMPAIGN_ENDDATE',
      })));
    } else if (value.isSameOrBefore(startDateValue)) {
      return Promise.reject(new Error(intl.formatMessage({
        id: 'campaigns.rules.CAMPAIGN_ENDDATE_BEFORE_STARTDATE',
      })));
    }
    return Promise.resolve();
  };

  const getViewLink = () => {
    if (worklistId === planningBoardWorklistId) {
      window.open(`/planning-board/${worklistId}/view/${selectedView}`);
      return;
    } else if (worklistId === ticketWorklistId) {
      window.open(`/tickets/view/${selectedView}`);
      return;
    }
    window.open(`/worklists/${worklistId}/view/${selectedView}`);
  };

  return (
    <SidePanel
      width={'40%'}
      title={intl.formatMessage({
        id: `campaigns.${params.id ? 'EDIT_CAMPAIGN' : 'ADD_CAMPAIGN'}`,
      })}
      onPanelClose={close}
      contentOverflow={false}
    >
      {isSidePanelLoading ?
        <Spinner /> :
        <Form
          layout='horizontal'
          onFinish={handleSave}
          onFieldsChange={() => setIsFormTouched(true)}
          requiredMark={false}
          form={form}
          disabled={isUpdatingCampaign}
        >
          <StyledRow>
            <Col span={22}>
              <FormField
                label={intl.formatMessage({
                  id: 'campaigns.edit.TITLE',
                })}
                name='title'
                initialValue=''
                rules={[{
                  required: true,
                  message: intl.formatMessage({ id: 'campaigns.rules.CAMPAIGN_TITLE' }),
                }]}
              >
                <Input data-test-id='input-campaign-title' />
              </FormField>
            </Col>
          </StyledRow>
          <StyledRow>
            <Col span={22}>
              <FormField
                label={intl.formatMessage({
                  id: 'campaigns.edit.WORKLIST',
                })}
                name='worklist'
                // eslint-disable-next-line no-undefined
                initialValue={undefined}
                rules={[{
                  required: true,
                  message: intl.formatMessage({ id: 'campaigns.rules.CAMPAIGN_WORKLIST' }),
                }]}
              >
                <Dropdown
                  values={wizardData?.worklists || []}
                  valueKey='id'
                  valueLabel='name'
                  onChange={e => setWorklistId(e)}
                  disabled={!!params.id}
                  placeholder={
                    `${intl.formatMessage({ id: 'campaigns.edit.WORKLIST_PLACEHOLDER' })}...`
                  }
                  data-test-id='dropdown-campaign-worklist-type'
                />
              </FormField>
            </Col>
          </StyledRow>
          <StyledRowWithLink>
            <Col span={22}>
              <FormField
                label={intl.formatMessage({
                  id: 'campaigns.edit.WORKLISTVIEWS',
                })}
                name='worklistView'
                // eslint-disable-next-line no-undefined
                initialValue={undefined}
                rules={[{
                  required: true,
                  message: intl.formatMessage({ id: 'campaigns.rules.CAMPAIGN_WORKLISTVIEWS' }),
                }]}
              >
                <StyledDropdown
                  values={worklistViews}
                  valueKey='id'
                  valueLabel='name'
                  disabled={!worklistId || !!params?.id}
                  onChange={setSelectedView}
                  placeholder={
                    !worklistId ?
                      `${intl.formatMessage({
                        id: 'campaigns.edit.SELECT_WORKLIST_PLACEHOLDER',
                      })}` :
                      `${intl.formatMessage({ id: 'campaigns.edit.WORKLISTVIEWS_PLACEHOLDER' })}...`
                  }
                  data-test-id='dropdown-campaign-worklist-view'
                />
              </FormField>
              {selectedView &&
                <OpenLink onClick={getViewLink}>
                  {intl.formatMessage({ id: 'campaigns.edit.OPEN' })}
                  <Icon type='arrow-up-right-from-square' />
                </OpenLink>
              }
            </Col>
          </StyledRowWithLink>
          <StyledRow>
            <Col span={22}>
              <StyledFormField
                label={`${intl.formatMessage({
                  id: 'campaigns.edit.GROUP_BY_PLANNER_GROUP',
                })}:`}
                name='groupByPlannerGroup'
                initialValue={false}
                valuePropName='checked'
                colon={false}
              >
                <Checkbox
                  disabled={!!params?.id || !canGroupByPlannerGroup}
                  data-test-id='checkbox-campaign-group-by-planner-group'
                />
              </StyledFormField>
            </Col>
          </StyledRow>
          <StyledRow>
            <Col span={22}>
              <FormField
                label={intl.formatMessage({
                  id: 'campaigns.edit.CALCULATE',
                })}
                name='calculationAmount'
                initialValue={null}
                rules={[{
                  required: true,
                  message: intl.formatMessage({ id: 'campaigns.rules.CAMPAIGN_CALCULATE' }),
                }]}
                wrapperCol={{ span: 5 }}
              >
                <Dropdown
                  values={calculationOptions}
                  valueKey='id'
                  valueLabel='description'
                  data-test-id='dropdown-campaign-calculate'
                />
              </FormField>
            </Col>
          </StyledRow>
          <SubForm>
            <StyledRow>
              <Col span={18}>
                <FormField
                  label={intl.formatMessage({
                    id: 'campaigns.edit.FREQUENCY',
                  })}
                  name='frequency'
                  initialValue={null}
                  rules={[{
                    required: true,
                    message: intl.formatMessage({ id: 'campaigns.rules.CAMPAIGN_FREQUENCY' }),
                  }]}
                  wrapperCol={{ span: 8 }}
                >
                  <Dropdown
                    values={wizardData?.frequencies || []}
                    valueKey='id'
                    valueLabel='name'
                    data-test-id='dropdown-campaign-frequency'
                  />
                </FormField>
              </Col>
            </StyledRow>
            <StyledRow>
              <Col span={18}>
                <DateFormField
                  label={intl.formatMessage({
                    id: 'campaigns.edit.STARTING',
                  })}
                  name='startDate'
                  initialValue={null}
                  rules={[{
                    required: true,
                    message: intl.formatMessage({ id: 'campaigns.rules.CAMPAIGN_STARTING' }),
                  }]}
                  wrapperCol={{ span: 8 }}
                >
                  <DatePicker
                    allowClear={false}
                    suffixIcon={<DownOutlined />}
                    disabledDate={current => current.isBefore(
                      moment().set({ hours: 23, minutes: 59, seconds: 59 }),
                    )}
                    data-test-id='input-campaign-start-date'
                  />
                </DateFormField>
              </Col>
            </StyledRow>
            <StyledRow>
              <Col span={18}>
                <FormField
                  label={intl.formatMessage({
                    id: 'campaigns.edit.ENDING',
                  })}
                  name='useOccurrences'
                >
                  <Radio.Group>
                    <Space direction='vertical'>
                      <Radio value={false} data-test-id='radiobtn-campaign-end-on-option'>
                        <StyledFlex ffrn aic>
                          <div>
                            {intl.formatMessage({
                              id: 'campaigns.edit.ON',
                            })}
                          </div>
                          <FormField
                            name='endDate'
                            initialValue={null}
                            rules={[{ validator: endDateValidators }]}
                          >
                            <DatePicker
                              allowClear={false}
                              suffixIcon={<DownOutlined />}
                              disabledDate={current => current.isSameOrBefore(startDateValue)}
                              useBodyAsParent={true}
                              disabled={useOccurrences}
                              onClick={e => e.preventDefault()}
                              data-test-id='input-campaign-end-date'
                            />
                          </FormField>
                        </StyledFlex>
                      </Radio>
                      <Radio value={true} data-test-id='radiobtn-campaign-end-after-option'>
                        <StyledFlex ffrn aic>
                          <div>
                            {intl.formatMessage({
                              id: 'campaigns.edit.AFTER',
                            })}
                          </div>
                          <OccurencesFormField
                            name='occurrences'
                            initialValue={null}
                            rules={[{
                              required: useOccurrences,
                              message: intl.formatMessage({
                                id: 'campaigns.rules.CAMPAIGN_OCCURRENCES',
                              }),
                            }]}
                          >
                            <InputNumber
                              min={1}
                              disabled={!useOccurrences}
                              data-test-id='input-campaign-end-after-occurences'
                            />
                          </OccurencesFormField>
                          <div>
                            {intl.formatMessage({
                              id: 'campaigns.edit.OCCURRENCES',
                            })}
                          </div>
                        </StyledFlex>
                      </Radio>
                    </Space>
                  </Radio.Group>
                </FormField>
              </Col>
            </StyledRow>
          </SubForm>
          <StyledRow>
            <Col span={22}>
              <FormField
                label={intl.formatMessage({
                  id: 'campaigns.edit.SCOPES',
                })}
                name='scopes'
                initialValue={[]}
                rules={[{
                  required: true,
                  message: intl.formatMessage({ id: 'campaigns.rules.CAMPAIGN_SCOPES' }),
                }]}
              >
                <Dropdown
                  multiSelect={true}
                  values={wizardData?.scopes || []}
                  valueKey='id'
                  valueLabel='name'
                  placeholder={
                    `${intl.formatMessage({ id: 'campaigns.edit.SCOPES_PLACEHOLDER' })}...`
                  }
                  data-test-id='dropdown-campaign-customer-center'
                />
              </FormField>
            </Col>
          </StyledRow>
          <StyledRow>
            <Col span={22}>
              <FormField
                label={intl.formatMessage({
                  id: 'campaigns.edit.LOCATIONS',
                })}
                name='locations'
                initialValue={[]}
                rules={[{
                  required: true,
                  message: intl.formatMessage({ id: 'campaigns.rules.CAMPAIGN_LOCATIONS' }),
                }]}
              >
                <Dropdown
                  multiSelect={true}
                  values={wizardData?.locations || []}
                  valueKey='id'
                  valueLabel='name'
                  placeholder={
                    `${intl.formatMessage({ id: 'campaigns.edit.LOCATIONS_PLACEHOLDER' })}...`
                  }
                  data-test-id='dropdown-campaign-locations'
                />
              </FormField>
            </Col>
          </StyledRow>
          <StyledRow>
            <Col span={22}>
              <FormField
                name='instruction'
                label={intl.formatMessage({
                  id: 'campaigns.edit.INSTRUCTIONS',
                })}
                initialValue=''
                data-test-id='input-campaign-instructions'
              >
                <RichTextEditor width='100%' />
              </FormField>
            </Col>
          </StyledRow>
          <StyledRow>
            <Col span={22}>
              <FormField
                label={intl.formatMessage({
                  id: 'campaigns.edit.ACTIONS',
                })}
                name='actions'
                initialValue={[]}
                rules={[{
                  required: true,
                  message: intl.formatMessage({ id: 'campaigns.rules.CAMPAIGN_ACTIONS' }),
                }]}
              >
                <Dropdown
                  multiSelect={true}
                  values={worklistActions}
                  valueKey='id'
                  valueLabel='name'
                  disabled={!worklistId}
                  placeholder={
                    !worklistId ?
                      `${intl.formatMessage({
                        id: 'campaigns.edit.SELECT_WORKLIST_PLACEHOLDER',
                      })}` :
                      `${intl.formatMessage({ id: 'campaigns.edit.ACTIONS_PLACEHOLDER' })}...`
                  }
                  data-test-id='dropdown-campaign-actions'
                />
              </FormField>
            </Col>
          </StyledRow>
          <Buttons marginTop={20}>
            <StyledButton
              onClick={close}
              disabled={isUpdatingCampaign}
              data-test-id='btn-campaign-cancel'
            >
              {intl.formatMessage({ id: 'common.CANCEL' })}
            </StyledButton>
            <StyledButton
              htmlType='submit'
              type='secondary'
              disabled={isUpdatingCampaign || !isFormTouched}
              data-test-id='btn-campaign-save'
            >
              {intl.formatMessage({
                id: `campaigns.${params.id ? 'SAVE_CHANGES' : 'ADD_CAMPAIGN'}`,
              })}
            </StyledButton>
          </Buttons>
        </Form>
      }
    </SidePanel>
  );
};

EditCampaignSidepanel.propTypes = {
  intl: PropTypes.object,
};

export default injectIntl(EditCampaignSidepanel);
